import React, { type ReactElement, useCallback, useState } from "react";
import { graphql, useQueryLoader } from "react-relay";
import { ServerSearchListbox } from "@olivahealth/oli-ui";
import { debounce } from "lodash";
import { OrganisationSelectorQuery as OrganisationSelectorQueryType } from "./__generated__/OrganisationSelectorQuery.graphql";

const OrganisationSelectorQuery = graphql`
  query OrganisationSelectorQuery {
    organisations {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;

interface OrganisationOption {
  id: string;
  name: string;
}

interface ExtendedOption {
  id: string;
  value: string;
  label: string;
  data?: OrganisationOption;
}

interface Props {
  onOrganisationSelected: (org: OrganisationOption) => void;
  placeholder?: string;
}

export default function OrganisationSelector({
  onOrganisationSelected,
  placeholder,
}: Props): ReactElement {
  const [organisationsQueryReference, loadOrganisationsQuery] =
    useQueryLoader<OrganisationSelectorQueryType>(OrganisationSelectorQuery);
  const [searchTerm, setSearchTerm] = useState("");

  // Load organisations on mount
  React.useEffect(() => {
    loadOrganisationsQuery({}, { fetchPolicy: "network-only" });
  }, [loadOrganisationsQuery]);

  // Debounced search handler
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((term: string) => {
      setSearchTerm(term.toLowerCase());
    }, 300),
    [],
  );

  return (
    <ServerSearchListbox
      query={OrganisationSelectorQuery}
      queryReference={organisationsQueryReference}
      transformDataToOptions={(data) => {
        const typedData = data as OrganisationSelectorQueryType["response"];
        let organisations =
          typedData?.organisations?.edges?.map((edge) => edge.node) || [];

        // Client-side filtering by search term
        if (searchTerm) {
          organisations = organisations.filter((org) =>
            org.name.toLowerCase().includes(searchTerm),
          );
        }

        return organisations.map((org) => ({
          id: org.id,
          value: org.id,
          label: org.name,
          data: {
            id: org.id,
            name: org.name,
          },
        })) as ExtendedOption[];
      }}
      onSearchChange={(inputText) => {
        if (inputText !== undefined) {
          debouncedSearch(inputText);
        }
      }}
      setSelected={(option) => {
        const extendedOption = option as ExtendedOption;
        if (!extendedOption?.value || !extendedOption.data) return;
        onOrganisationSelected(extendedOption.data);
      }}
      placeholder={placeholder ?? "Select an organisation"}
    />
  );
}
